import { useState, useEffect } from 'react';

/** Threshold: when window size difference exceeds this, consider DevTools open (panel takes space) */
const DEVTOOLS_SIZE_THRESHOLD = 160;

/**
 * Heuristic detection of DevTools: when open, outerWidth/Height often differ from innerWidth/Height
 * by more than the threshold. Not 100% reliable (browser-dependent).
 */
function detectDevToolsOpen(): boolean {
  if (typeof window === 'undefined') return false;
  const widthDiff = window.outerWidth - window.innerWidth;
  const heightDiff = window.outerHeight - window.innerHeight;
  return widthDiff > DEVTOOLS_SIZE_THRESHOLD || heightDiff > DEVTOOLS_SIZE_THRESHOLD;
}

export interface DevToolsGuardResult {
  /** True when URL has ?devTools=true (inspect allowed, no overlay) */
  devToolsAllowed: boolean;
  /** True when DevTools are detected and devToolsAllowed is false (show blocking overlay) */
  devToolsOpen: boolean;
}

/**
 * Use on exam pages. When devTools=true is in the URL, returns devToolsAllowed: true and does not detect.
 * Otherwise runs a periodic DevTools check; when detected, devToolsOpen becomes true so the page can show a blocking overlay.
 */
export function useDevToolsGuard(searchParams: URLSearchParams): DevToolsGuardResult {
  const devToolsAllowed = searchParams.get('devTools') === 'true';
  const [devToolsOpen, setDevToolsOpen] = useState(false);

  useEffect(() => {
    if (devToolsAllowed) {
      setDevToolsOpen(false);
      return;
    }
    const interval = setInterval(() => {
      setDevToolsOpen(detectDevToolsOpen());
    }, 500);
    return () => clearInterval(interval);
  }, [devToolsAllowed]);

  return { devToolsAllowed, devToolsOpen: devToolsAllowed ? false : devToolsOpen };
}
